import { useContext, useEffect, useState } from "react";
import Moment from "react-moment";
import "moment-timezone";
import "moment/locale/it";
import Api from "./api/api";
import { TableContext, SettingsContext, CartContext, ShopContext, socket } from "../App";
import { socketMandantKey } from "../Socket";
import currencyFormat, { canShowReceiptButton, getStatusColor, getStatusLabel, textLabel } from "./functions/functions";
import "../css/Orders.css";
import CloseButton from "./CloseButton";
import Label from "./Label";
import moment from "moment/moment";
import OrderTicketsModal from "./OrderTicketsModal";

function Orders({ setOrdersVisible }) {
	const { settings, lang, labels, initTablePayment, userID, setLoading, singleOrderMode, monoOrder } =
		useContext(SettingsContext);
	const { table } = useContext(TableContext);
	const { cart } = useContext(CartContext);
	const [orders, setOrders] = useState(false);
	const [ticketsModalOrder, setTicketsModalOrder] = useState(null);
	const { shop } = useContext(ShopContext);

	// Niente polling: la lista si ricarica quando l'API avvisa che un ordine di questo carrello (o di questo utente in
	// modalita' ordine singolo) e' cambiato, su un topic dedicato con lo stesso parametro del filtro (api/class/socket.php
	// send_customer_orders_event), sugli eventi del tavolo e dopo una riconnessione (gli eventi nel frattempo sono persi).
	const ordersKey = singleOrderMode ? userID : cart?.cart_id;

	useEffect(() => {
		let stopped = false;
		let refreshTimer = null;
		let loading = false;
		let reloadAgain = false;

		const load = async () => {
			if (loading) {
				reloadAgain = true;
				return;
			}
			loading = true;
			try {
				const rows = await load_orders();
				if (!stopped) setOrders(rows);
			} catch (e) {
				console.log("load_orders", e);
			} finally {
				loading = false;
			}
			if (reloadAgain && !stopped) {
				reloadAgain = false;
				load();
			}
		};

		const scheduleRefresh = () => {
			if (refreshTimer) return;
			refreshTimer = setTimeout(() => {
				refreshTimer = null;
				if (!stopped) load();
			}, 300);
		};

		const topic = settings?.mandant?.id && ordersKey
			? "m" + socketMandantKey(settings.mandant.id) + "." + (singleOrderMode ? "temp_customer_orders" : "self_cart_orders") + ".*." + ordersKey
			: null;
		const onConnect = () => {
			if (topic) socket.emit("subscribe", topic);
			scheduleRefresh();
		};
		const onSocketEvents = (message) => {
			if (message.table === "self_cart_orders" || message.table === "temp_customer_orders") {
				if (message.id === ordersKey) scheduleRefresh();
			} else if (message.table === "tables") scheduleRefresh();
		};

		socket.on("connect", onConnect);
		socket.on("events", onSocketEvents);
		if (topic && socket.connected) socket.emit("subscribe", topic);
		load();

		return () => {
			stopped = true;
			clearTimeout(refreshTimer);
			socket.off("connect", onConnect);
			socket.off("events", onSocketEvents);
		};
	}, [ordersKey, singleOrderMode, settings?.mandant?.id]);

	const load_orders = async () => {
		console.log("load_orders");
		let request = {
			lang: lang,
			src: [],
		};
		if (singleOrderMode) {
			request.src.push({
				name: "temp_customer_id",
				value: userID,
				compare: "equal",
			});
			request.src.push({
				name: "delivery_date",
				value: moment().format("YYYY-MM-DD"),
				compare: "date",
			});
		} else {
			request.src.push({
				name: "self_cart_id",
				value: cart.cart_id,
				compare: "equal",
			});
		}
		const response = await Api.post("/orders/list/", request);
		//console.log(response);
		return response.rows;
	};

	const loadBillUrl = async (order) => {
		if (shop?.scloby_active == 1) {
			setLoading(true);
			const response = await Api.post("/orders/get_bill_url/" + order.id + "/");
			setLoading(false);
			if (response.success == 0) {
				alert(response.error);
				return;
			}
			window.open(response.bill_url, "_blank");
			return;
		}

		setLoading(true);
		const response = await Api.post("/orders/get/" + order.id + "/");
		setLoading(false);
		if (response.success == 0) {
			alert(response.error);
			return;
		}
		const billUrl = typeof response.data?.bill_url === "string" ? response.data.bill_url.trim() : "";
		if (!billUrl) {
			alert(textLabel(labels, 173, lang) || "Scontrino non disponibile");
			return;
		}
		window.open(billUrl, "_blank");
	};

	const parseNicknames = (rawNickname) => {
		if (!rawNickname) return [];
		try {
			const parsed = JSON.parse(rawNickname);
			if (!Array.isArray(parsed)) return [];
			return parsed
				.map((item) => ({
					nickname: item?.nickname ? String(item.nickname).trim() : "",
					qty: parseInt(item?.qty ?? 0),
				}))
				.filter((item) => item.nickname && item.qty > 0);
		} catch {
			return [];
		}
	};

	const normalizeNicknameLabel = (rawNickname) => {
		const parsed = parseNicknames(rawNickname);
		if (parsed.length === 0) return textLabel(labels, 172, lang) || "Cliente";
		return parsed.map((item) => `${item.nickname} x${item.qty}`).join(", ");
	};

	const groupByUserNickname = (items) => {
		const map = new Map();
		for (const item of items) {
			const label = normalizeNicknameLabel(item?.user_nickname);
			if (!map.has(label)) map.set(label, []);
			map.get(label).push(item);
		}
		return Array.from(map.entries());
	};

	const shouldGroupByCustomer =
		settings.selfordering.enable_group_by_customer == 1 && !singleOrderMode && !monoOrder;
	return (
		<>
			<div className="popup orders">
				<div className="content">
					<div className="orders-header">
						<CloseButton onClick={() => setOrdersVisible(false)} />
						<h3 className="mb-0">
							{settings.selfordering.table_label == 1 && (
								<>
									<Label number={22} />{" "}
								</>
							)}
							{table.name}
						</h3>
					</div>
					<div className="orders-scroll">
						{orders && orders.length === 0 && (
							<div className="text-center"><Label number={174} /></div>
						)}
						{orders &&
							orders.map((order, n) => (
								<div key={n} className="orders-content">
									<div className="orders-subtitle">
										<div>
											<Moment format={"LT"} locale="it">
												{order.delivery_date}
											</Moment>{" "}
											- N°{order.number} (ID:{order.id})
										</div>
										<div>
											{singleOrderMode && canShowReceiptButton(order, shop) && (
												<button onClick={() => loadBillUrl(order)}>
													<Label number={171} />
												</button>
											)}
											{(monoOrder ? settings.selfordering.enable_pickup_receipts_mono == 1 : settings.selfordering.enable_pickup_receipts_single_order == 1) && !order.self_cart_id && (
												<button onClick={() => setTicketsModalOrder(order)}>
													<Label number={156} />
												</button>
											)}
										</div>
									</div>
									<div className="orders-products">
										{shouldGroupByCustomer
											? groupByUserNickname(order.products).map(([label, items]) => (
													<div key={label} className="orders-group">
														<div className="orders-group-title">{label}</div>
														<div className="orders-group-products">
															{items.map((product, i) => (
																<div key={i}>
																	<StatusTag status={product.status} />
																	<div>
																		{product.qty}x {product.name}
																	</div>
																</div>
															))}
														</div>
													</div>
											  ))
										: order.products.map((product, i) => (
													<div key={i}>
														<StatusTag status={product.status} />
														<div>
															{product.qty}x {product.name}
															<div className="small">
																{normalizeNicknameLabel(product.user_nickname)}
															</div>
														</div>
													</div>
											  ))}
									</div>
								</div>
							))}
					</div>
					{!singleOrderMode && (
						<div className="orders-footer">
							{settings.selfordering.hide_total == 0 && (
								<div className="orders-total">
									<Label number={25} />:{" "}
									<span>
										{currencyFormat(cart.table_total, false, shop.currency)}
									</span>
								</div>
							)}
							<div className="buttons">
								<button onClick={() => setOrdersVisible(false)}>
									<Label number={6} />
								</button>
								{settings.selfordering.disable_table_checkout != 1 && (
									<button
										className="button-primary"
										onClick={() => initTablePayment()}
									>
										<Label number={24} />
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
			{ticketsModalOrder && (
				<OrderTicketsModal
					order={ticketsModalOrder}
					onClose={() => setTicketsModalOrder(null)}
				/>
			)}
		</>
	);
}

function StatusTag({ status }) {
	return (
		<div className="status-tab" style={{ backgroundColor: getStatusColor(status) }}>
			<Label number={getStatusLabel(status)} />
		</div>
	);
}

export default Orders;
